/* STRIXON analytics: consent-gated vendor adapters and current-page event islands.
 * Edit this source; build-js.mjs generates the served /js/analytics.js.
 * Internal commerce payloads use store prices including VAT. googleParams converts
 * them to GA4 net merchandise revenue; Meta/TikTok retain gross order values.
 * Page islands wait for consent; unconsented interaction events are not replayed.
 * Purchase SDK hand-offs are deduplicated per vendor in this browser tab/session.
 * This is best-effort browser tracking, not proof of vendor receipt.
 */

(() => {
  'use strict';

  // -------------------------------------------------------------
  // 0) The canonical live consent flag (single source of truth).
  //
  // The SERVER renders the initial decision into window.__strxConsent
  // ('none' | 'granted' | 'denied') in _TrackingHead, and the consent banner
  // (site.js) updates it + dispatches `strx:consent-changed` {granted} on
  // Accept / Reject. This module mirrors that into ONE module-level flag,
  // initialised from the server flag and kept live on every consent change —
  // so the Meta fan-out (responsibility #1) and the dev HUD (responsibility #3)
  // both read the SAME truth rather than re-deriving it ad hoc, and a
  // same-session Accept takes effect WITHOUT a page reload.
  //
  const readConsent = () => {
    const v = window.__strxConsent;
    return (v === 'granted' || v === 'denied') ? v : 'none';
  };

  // Live, mutable — the one flag every consent-sensitive reader checks.
  let consentState = readConsent();
  const isGranted = () => window.strxPrivacy ? window.strxPrivacy.allows('analytics') : consentState === 'granted';
  const adsGranted = () => window.strxPrivacy ? window.strxPrivacy.allows('advertising') : consentState === 'granted';

  // A successful SDK hand-off is deduplicated per vendor in this tab/session.
  // This also covers Accept on an initially undecided purchase page, whose
  // server marker deliberately was not consumed. SDK hand-off is best effort,
  // not an acknowledgement of collection by a third party.
  const purchases = new Set();
  const deliver = (vendor, name, payload, send) => {
    const id = name === 'purchase' && typeof payload.transaction_id === 'string'
      ? payload.transaction_id : '';
    const key = id ? 'strx.purchase.v1:' + vendor + ':' + id : '';
    try {
      if (key && purchases.has(key)) return;
      try { if (key && sessionStorage.getItem(key) === '1') return; } catch (_) { /* storage unavailable */ }
      send();
      if (key) {
        purchases.add(key);
        try { sessionStorage.setItem(key, '1'); } catch (_) { /* memory still deduplicates */ }
      }
    } catch (_) { /* one vendor must never break commerce or another vendor */ }
  };

  // Internal commerce values remain the store's VAT-inclusive SAR amounts.
  // GA4 requires merchandise revenue excluding shipping/tax and net item prices.
  // Use the rate rendered from OrderPricing, not a second tax configuration.
  const googleParams = (payload) => {
    const result = { ...payload };
    const rate = Number(window.__strxVatRate);
    if (!Number.isFinite(rate) || rate < 0 || !Array.isArray(payload.items)) return result;
    const factor = 1 + rate;
    const round = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
    result.items = payload.items.filter((item) => item && typeof item === 'object').map((item) => {
      const copy = { ...item };
      // Keep precision until the event total is rounded to avoid multiplying
      // a per-unit rounding error by a large quantity.
      if (Number.isFinite(copy.price)) copy.price = Math.round(copy.price / factor * 1e6) / 1e6;
      // A coupon line arrives with its DISCOUNTED price + a per-unit discount (Google's
      // item spec), so the value sum below is already net of the coupon; the discount
      // is converted to the same net-of-VAT basis for consistency.
      if (Number.isFinite(copy.discount)) copy.discount = Math.round(copy.discount / factor * 1e6) / 1e6;
      return copy;
    });
    if (Number.isFinite(payload.value)) {
      result.value = round(result.items.reduce((sum, item) =>
        sum + (Number.isFinite(item.price) ? item.price : 0) * (item.quantity || 1), 0));
    }
    if (Number.isFinite(payload.shipping)) result.shipping = round(payload.shipping / factor);
    return result;
  };

  // -------------------------------------------------------------
  // 1) The strxTrack event seam.
  //
  // Defined eagerly at module load (this file is `defer`, so the DOM is
  // parsed, but gtag.js may still be loading) — gtag()'s dataLayer shim
  // already exists from _TrackingHead, so any forwarded event is harmlessly
  // queued until gtag.js finishes. We keep a tiny in-memory ring of the
  // most recent events purely so the dev HUD (and a curious console) can
  // inspect them; it holds NO PII beyond whatever a caller passes and is
  // never persisted.
  // -------------------------------------------------------------
  const HUD_EVENT = 'strx:track';       // internal CustomEvent the HUD listens for
  const RING_MAX = 25;                  // cap the in-memory recent-event ring

  // The recent-events ring is only meaningful with the dev HUD; in prod the
  // HUD root is absent and nothing reads it, so the ring stays a cheap array.
  const recent = [];

  const remember = (name, payload) => {
    recent.push({ name, payload, at: Date.now() });
    if (recent.length > RING_MAX) recent.shift();
  };

  // -------------------------------------------------------------
  // 1a) The Meta (Facebook) Pixel fan-out  (Phase 12 / Step 6).
  //
  // The GA4-canonical event flowing through strxTrack is MAPPED here onto a
  // Meta STANDARD event and forwarded via fbq — the single, documented Meta
  // extension point. It is a thin, pure translation; it adds NO new page
  // instrumentation. Every Meta event therefore inherits the existing
  // strxTrack call's timing + guarantees (notably: `purchase` is the
  // server-gated exactly-once Paid-confirmation island, so Meta `Purchase`
  // is exactly-once for free).
  //
  // HARD GATE (belt-and-suspenders on top of the loader's consent revoke):
  // we fire fbq ONLY when window.fbq exists AND consent === 'granted'. A
  // 'none' / 'denied' visitor produces ZERO fbq calls from here, so even if
  // the loader gate were ever bypassed, no Meta traffic leaves an unconsented
  // page. Never throws — a Meta failure must never break the page or the GA4
  // forward above.
  //
  // The GA4 → Meta name + parameter translation lives ENTIRELY in
  // META_EVENT_MAP + buildMetaParams below (one clearly-commented place).
  // Events with no clean Meta standard event (view_item_list / view_cart /
  // add_shipping_info / page_view) are INTENTIONALLY absent from the map and
  // produce no Meta event — we do not invent junk custom events.
  //
  //   GA4 event       → Meta standard event
  //   ─────────────────────────────────────
  //   view_item       → ViewContent
  //   add_to_cart     → AddToCart
  //   begin_checkout  → InitiateCheckout
  //   search          → Search
  //   purchase        → Purchase   (+ eventID = transaction_id for dedup)
  //
  //   page_view is fired by Meta's OWN base snippet (NOT routed here),
  //   mirroring GA4's automatic page_view.
  // -------------------------------------------------------------
  const META_EVENT_MAP = {
    view_item: 'ViewContent',
    add_to_cart: 'AddToCart',
    begin_checkout: 'InitiateCheckout',
    search: 'Search',
    purchase: 'Purchase',
  };

  // Coerce a GA4 items[] array into Meta's content shape. Returns
  // { contentIds, contents, numItems } — all derived from the SAME items, so
  // content_ids[i] always lines up with contents[i]. Defensive against a
  // missing / malformed items array (→ empty shape, never throws).
  const metaContentsFromItems = (items) => {
    const contentIds = [];
    const contents = [];
    let numItems = 0;
    if (Array.isArray(items)) {
      for (let i = 0; i < items.length; i += 1) {
        const it = items[i];
        if (!it || typeof it !== 'object') continue;
        const id = it.item_id;
        if (typeof id !== 'string' || id.length === 0) continue;
        const qty = (typeof it.quantity === 'number' && it.quantity > 0) ? it.quantity : 1;
        contentIds.push(id);
        contents.push({ id: id, quantity: qty, item_price: it.price });
        numItems += qty;
      }
    }
    return { contentIds, contents, numItems };
  };

  // Translate a GA4-canonical payload into Meta standard-event params. Pure;
  // returns ONLY the params object (the eventID, used solely by Purchase, is
  // returned separately so the caller can pass it as fbq's 4th arg). The GA4
  // payload is never mutated.
  const buildMetaParams = (gaName, payload) => {
    const p = payload || {};

    // Search carries no commerce shape — just the query term.
    if (gaName === 'search') {
      return { params: { search_string: typeof p.search_term === 'string' ? p.search_term : '' }, eventId: null };
    }

    // The value-bearing commerce events (view_item / add_to_cart /
    // begin_checkout / purchase) all share { currency, value, items[] }; we
    // always send currency (defaulting to SAR — a value event with no currency
    // is the #1 Meta misconfiguration) + the derived content shape.
    const shape = metaContentsFromItems(p.items);
    const params = {
      content_type: 'product',
      content_ids: shape.contentIds,
      contents: shape.contents,
      currency: typeof p.currency === 'string' && p.currency.length > 0 ? p.currency : 'SAR',
    };

    // value only when the GA4 payload actually carried one (never a stray
    // `value: undefined` on the wire).
    if (typeof p.value === 'number') params.value = p.value;
    if (shape.numItems > 0) params.num_items = shape.numItems;

    // ViewContent is a single product — surface its name when present (optional,
    // improves Meta reporting; never required).
    if (gaName === 'view_item' && Array.isArray(p.items) && p.items[0] && typeof p.items[0].item_name === 'string') {
      params.content_name = p.items[0].item_name;
    }

    // Purchase carries the order number as the dedup eventID (browser↔CAPI key
    // for the future Conversions API upgrade; harmless now, essential later).
    const eventId = (gaName === 'purchase' && typeof p.transaction_id === 'string' && p.transaction_id.length > 0)
      ? p.transaction_id
      : null;

    return { params, eventId };
  };

  // Fan the GA4-canonical event out to Meta. No-op unless this GA4 event maps
  // to a Meta standard event AND fbq exists AND consent is granted. Wrapped so
  // a Meta failure can NEVER break the page or the GA4 forward.
  const fanOutToMeta = (gaName, payload) => {
    const metaName = META_EVENT_MAP[gaName];
    if (!metaName) return;                                   // unmapped GA4 event → no Meta event
    if (typeof window.fbq !== 'function') return;            // Meta not loaded (off / excluded route)
    if (!adsGranted()) return;                               // Advertising preference — no fbq before grant
    try {
      const { params, eventId } = buildMetaParams(gaName, payload);
      if (eventId) {
        deliver('meta', gaName, payload, () => window.fbq('track', metaName, params, { eventID: eventId }));
      } else {
        window.fbq('track', metaName, params);
      }
    } catch (_) { /* a Meta fan-out fault must never break the page */ }
  };

  // -------------------------------------------------------------
  // 1a-bis) The TikTok Pixel fan-out  (Phase 12 / Step 7).
  //
  // The TWIN of the Meta fan-out above (read it first). The same GA4-canonical
  // event flowing through strxTrack is MAPPED here onto a TikTok STANDARD event
  // and forwarded via ttq.track — the single, documented TikTok extension point.
  // It is a thin, pure translation; it adds NO new page instrumentation, so every
  // TikTok event inherits the existing strxTrack call's timing + guarantees
  // (notably: `purchase` is the server-gated exactly-once Paid-confirmation
  // island, so TikTok `Purchase` is exactly-once for free).
  //
  // HARD GATE (belt-and-suspenders on top of the loader's ttq.holdConsent): we
  // fire ttq ONLY when window.ttq exists AND consent === 'granted' (the SAME
  // isGranted() flag the Meta fan-out uses — no third consent flag). A 'none' /
  // 'denied' visitor produces ZERO ttq calls from here. Never throws — a TikTok
  // failure must never break the page, the GA4 forward, OR the Meta fan-out.
  //
  // The GA4 → TikTok name + parameter translation lives ENTIRELY in
  // TIKTOK_EVENT_MAP + buildTikTokParams below (one clearly-commented place).
  // Events with no clean TikTok standard event (view_item_list / view_cart /
  // add_shipping_info / page_view) are INTENTIONALLY absent from the map and
  // produce no TikTok event.
  //
  //   GA4 event       → TikTok standard event
  //   ───────────────────────────────────────
  //   view_item       → ViewContent
  //   add_to_cart     → AddToCart
  //   begin_checkout  → InitiateCheckout
  //   search          → Search
  //   purchase        → Purchase   (+ event_id = transaction_id for dedup)
  //
  //   page is fired by TikTok's OWN base snippet (ttq.page(), NOT routed here),
  //   mirroring GA4's automatic page_view and Meta's native PageView.
  //
  // NOTE on the May-2025 rename: TikTok renamed CompletePayment → Purchase. We
  // emit "Purchase"; the legacy name still auto-converts, and TikTok's Pixel
  // Helper may DISPLAY "CompletePayment" for the same event — that is expected,
  // not a bug.
  //
  // The PARAM shape is a SIBLING of Meta's, NOT the same object (TikTok differs):
  // TikTok nests content_id + content_name INSIDE each contents[] entry (Meta put
  // the ids at the top level as content_ids), and TikTok's Search key is `query`
  // (Meta used `search_string`). buildTikTokParams below is therefore its own
  // builder, not a reuse of buildMetaParams.
  // -------------------------------------------------------------
  const TIKTOK_EVENT_MAP = {
    view_item: 'ViewContent',
    add_to_cart: 'AddToCart',
    begin_checkout: 'InitiateCheckout',
    search: 'Search',
    purchase: 'Purchase',
  };

  // Coerce a GA4 items[] array into TikTok's contents shape:
  // [{ content_id, content_name, quantity, price }]. Defensive against a missing /
  // malformed items array (→ empty array, never throws). Unlike Meta, TikTok keeps
  // the id + name INSIDE each entry — there is no separate top-level id array.
  const tikTokContentsFromItems = (items) => {
    const contents = [];
    if (Array.isArray(items)) {
      for (let i = 0; i < items.length; i += 1) {
        const it = items[i];
        if (!it || typeof it !== 'object') continue;
        const id = it.item_id;
        if (typeof id !== 'string' || id.length === 0) continue;
        const qty = (typeof it.quantity === 'number' && it.quantity > 0) ? it.quantity : 1;
        const entry = { content_id: id, quantity: qty };
        if (typeof it.item_name === 'string' && it.item_name.length > 0) entry.content_name = it.item_name;
        if (typeof it.price === 'number') entry.price = it.price;
        contents.push(entry);
      }
    }
    return contents;
  };

  // Translate a GA4-canonical payload into TikTok standard-event params. Pure;
  // returns ONLY the params object (the event_id, used solely by Purchase, is
  // returned separately so the caller can pass it in ttq.track's options arg). The
  // GA4 payload is never mutated.
  const buildTikTokParams = (gaName, payload) => {
    const p = payload || {};

    // Search carries no commerce shape — just the query term. TikTok's key is
    // `query` (Meta used `search_string`).
    if (gaName === 'search') {
      return { params: { query: typeof p.search_term === 'string' ? p.search_term : '' }, eventId: null };
    }

    // The value-bearing commerce events (view_item / add_to_cart / begin_checkout
    // / purchase) all share { currency, value, items[] }; we always send currency
    // (defaulting to SAR — a value event with no currency is the #1 pixel
    // misconfiguration) + the derived contents shape + content_type:'product'.
    const params = {
      content_type: 'product',
      contents: tikTokContentsFromItems(p.items),
      currency: typeof p.currency === 'string' && p.currency.length > 0 ? p.currency : 'SAR',
    };

    // value only when the GA4 payload actually carried one (never a stray
    // `value: undefined` on the wire).
    if (typeof p.value === 'number') params.value = p.value;

    // Purchase carries the order number as the dedup event_id (browser↔Events API
    // key for the future server-side upgrade; harmless now, essential later).
    const eventId = (gaName === 'purchase' && typeof p.transaction_id === 'string' && p.transaction_id.length > 0)
      ? p.transaction_id
      : null;

    return { params, eventId };
  };

  // Fan the GA4-canonical event out to TikTok. No-op unless this GA4 event maps to
  // a TikTok standard event AND ttq exists AND consent is granted. Wrapped so a
  // TikTok failure can NEVER break the page, the GA4 forward, or the Meta fan-out.
  const fanOutToTikTok = (gaName, payload) => {
    const tikTokName = TIKTOK_EVENT_MAP[gaName];
    if (!tikTokName) return;                                  // unmapped GA4 event → no TikTok event
    if (!window.ttq || typeof window.ttq.track !== 'function') return; // TikTok not loaded
    if (!adsGranted()) return;                               // Advertising preference — no ttq before grant
    try {
      const { params, eventId } = buildTikTokParams(gaName, payload);
      if (eventId) {
        deliver('tiktok', gaName, payload, () => window.ttq.track(tikTokName, params, { event_id: eventId }));
      } else {
        window.ttq.track(tikTokName, params);
      }
    } catch (_) { /* a TikTok fan-out fault must never break the page */ }
  };

  /**
   * Fire a GA4-canonical analytics event.
   * @param {string} eventName  GA4 event name (e.g. 'view_item', 'add_to_cart').
   * @param {object} [payload]  GA4 event params (currency/value/items/…).
   */
  window.strxTrack = (eventName, payload) => {
    // Defensive: a missing/blank name is a programming error, not a crash.
    if (typeof eventName !== 'string' || eventName.length === 0) return;
    const params = (payload && typeof payload === 'object') ? payload : {};

    // Do not retain or transmit free-form searches resembling contact details.
    if (eventName === 'search' && /@|(?:\+?\d[\s().-]*){8,}/.test(params.search_term || '')) return;

    // Both consent and an active GA4 loader are required. Isolate SDK faults
    // so analytics can never interrupt shopping or another vendor's delivery.
    if (isGranted() && typeof window.__strxLoadGtag === 'function' && typeof window.gtag === 'function') {
      deliver('ga4', eventName, params, () => window.gtag('event', eventName, googleParams(params)));
    }

    // Meta fan-out — Phase 12 Step 6. Maps the GA4-canonical event onto a Meta
    // standard event and forwards via fbq, but ONLY when fbq exists AND consent
    // is granted (the hard gate). Self-defending (never throws).
    fanOutToMeta(eventName, params);

    // TikTok fan-out — Phase 12 Step 7. The twin of the Meta fan-out: maps the
    // GA4-canonical event onto a TikTok standard event and forwards via ttq.track,
    // ONLY when ttq exists AND consent is granted (the SAME hard gate / consent
    // flag). Self-defending (never throws) — independent of the GA4 forward and the
    // Meta fan-out above.
    fanOutToTikTok(eventName, params);
    try { window.__strxClarityEvent?.(eventName); } catch (_) { /* independent adapter */ }

    remember(eventName, params);

    // Notify the dev HUD (and any future first-party listener). Dispatched
    // always; in prod nothing listens, so it's a negligible no-op.
    try {
      document.dispatchEvent(new CustomEvent(HUD_EVENT, {
        detail: { name: eventName, payload: params },
      }));
    } catch (_) { /* very old engines without CustomEvent ctor — ignore */ }
  };

  // Expose the recent ring read-only-ish for console debugging.
  window.strxTrack.recent = () => recent.slice();

  // Semantic storefront actions use fixed names only. Never copy visible text,
  // hrefs, input values, customer IDs or form contents into these events.
  const interactionNames = Object.freeze([
    'strx_product_select', 'strx_gallery_next', 'strx_gallery_previous',
    'strx_gallery_thumbnail', 'strx_gallery_zoom', 'strx_gallery_close',
    'strx_search_open', 'strx_search_close', 'strx_search_submit', 'strx_search_result_select',
    'strx_cart_open', 'strx_cart_close', 'strx_cart_quantity_change',
    'strx_cart_remove', 'strx_cart_clear', 'strx_filter_open',
    'strx_filter_change', 'strx_filter_apply', 'strx_filter_clear',
    'strx_sort_change', 'strx_pagination', 'strx_menu_open', 'strx_menu_close',
    'strx_product_overview', 'strx_product_specs', 'strx_product_reviews',
    'strx_faq_toggle', 'strx_theme_change', 'strx_language_change',
    'strx_checkout_submit', 'strx_checkout_validation_error', 'strx_contact_intent', 'strx_stock_alert',
    'checkout_intent',
  ]);
  window.strxInteraction = name => {
    if (!isGranted() || !interactionNames.includes(name)) return;
    try { window.strxTrack(name); } catch (_) { /* never interrupt a user action */ }
  };
  window.strxInteraction.names = interactionNames;
  const action = name => window.strxInteraction(name);
  const clickActions = [
    ['.strx-suggest__row', 'strx_search_result_select'],
    ['.strx-pcard__link', 'strx_product_select'],
    ['.strx-gallery__nav--next, .strx-gallery-lightbox__nav--next', 'strx_gallery_next'],
    ['.strx-gallery__nav--prev, .strx-gallery-lightbox__nav--prev', 'strx_gallery_previous'],
    ['.strx-gallery__thumb', 'strx_gallery_thumbnail'],
    ['.strx-gallery__zoom', 'strx_gallery_zoom'],
    ['.strx-gallery-lightbox__close', 'strx_gallery_close'],
    ['.strx-fpanel__clear, .strx-afilters__chip a, .strx-afilters__clear', 'strx_filter_clear'],
    ['.strx-pager a', 'strx_pagination'],
    ['#pdp-tab-overview', 'strx_product_overview'],
    ['#pdp-tab-specs', 'strx_product_specs'],
    ['#pdp-tab-reviews', 'strx_product_reviews'],
    ['.strx-accordion__trigger', 'strx_faq_toggle'],
  ];
  document.addEventListener('click', event => {
    if (event.defaultPrevented) return;
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!target || target.closest('[disabled], [aria-disabled="true"]')) return;
    const match = clickActions.find(([selector]) => target.closest(selector));
    if (match) { action(match[1]); return; }
    const link = target.closest('a[href]');
    if (!link) return;
    try {
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin) return;
      if (/^\/(en|ar)\/checkout\/?$/i.test(url.pathname)) action('checkout_intent');
      if (/^\/(en|ar)\/contact\/?$/i.test(url.pathname)) action('strx_contact_intent');
    } catch (_) { /* invalid link cannot affect navigation */ }
  });
  document.addEventListener('change', event => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('[disabled]')) return;
    if (target.closest('.strx-shop__sort')) action('strx_sort_change');
    else if (target.closest('.strx-fpanel')) action('strx_filter_change');
  });
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.matches('.strx-fpanel')) action('strx_filter_apply');
    try {
      if (new URL(form.action, location.href).pathname.endsWith('/Home/SetLanguage')) action('strx_language_change');
    } catch (_) { /* no form values are read */ }
  });
  // Native invalid events do not bubble. Count one validation attempt per task,
  // not one event per invalid address field; never include the field or its value.
  let invalidCheckout = false;
  document.addEventListener('invalid', event => {
    if (invalidCheckout || !event.target.closest?.('.strx-checkout')) return;
    invalidCheckout = true;
    action('strx_checkout_validation_error');
    setTimeout(() => { invalidCheckout = false; }, 0);
  }, true);
  window.addEventListener('strx:theme-change', () => action('strx_theme_change'));
  for (const [eventName, suffix] of [['strx:overlay-open', 'open'], ['strx:overlay-close', 'close']]) {
    window.addEventListener(eventName, event => {
      const id = event.detail?.id;
      if (id === 'cart') action('strx_cart_' + suffix);
      else if (id === 'shop-filters-drawer' && suffix === 'open') action('strx_filter_open');
    });
  }

  // -------------------------------------------------------------
  // 1b) Apply independent preference changes to the vendor SDKs.
  // privacy.js owns the current cookie and installs the network barrier before
  // notifying us of withdrawal. Saved settings take effect on the next document;
  // other open tabs also reconcile through the first-party change signal.
  // Loader latches keep initial PageView and library loading idempotent.
  // The legacy design-system preview dispatches the same event for its demo.
  let _metaGranted = adsGranted() && (typeof window.fbq === 'function');

  const grantMeta = () => {
    if (_metaGranted) return;                         // grant + library fetch exactly once
    if (typeof window.fbq !== 'function') return;     // Meta not loaded (off / excluded route)
    _metaGranted = true;
    try {
      window.fbq('consent', 'grant');
      // Fetch the deferred library; draining its queue transmits the queued
      // init + PageView. We do NOT call fbq('track','PageView') here — the
      // bootstrap already queued exactly one, so re-firing would double it.
      if (typeof window.__strxLoadFbevents === 'function') window.__strxLoadFbevents();
    } catch (_) { /* a Meta grant fault must never break the page */ }
  };

  const revokeMeta = () => {
    // A live withdrawal (denied without a reload). Re-arm so a subsequent grant
    // can fire again, and tell Meta to stop transmitting.
    _metaGranted = false;
    if (typeof window.fbq !== 'function') return;
    try {
      window.fbq('consent', 'revoke');
    } catch (_) { /* never break the page */ }
  };

  // The TikTok grant/revoke — the exact twin of grantMeta/revokeMeta, off the SAME
  // trigger + the SAME consentState flag. ttq is an ARRAY (the queue shim) until
  // the library replays it, so we feature-detect grantConsent/revokeConsent rather
  // than assume the loaded SDK shape.
  let _tikTokGranted = adsGranted()
    && !!window.ttq && (typeof window.ttq.grantConsent === 'function');

  const grantTikTok = () => {
    if (_tikTokGranted) return;                          // grant + library fetch exactly once
    if (!window.ttq || typeof window.ttq.grantConsent !== 'function') return; // not loaded
    _tikTokGranted = true;
    try {
      window.ttq.grantConsent();
      // Fetch the deferred library; its replay transmits the queued load + page().
      // We do NOT call ttq.page() here — the bootstrap already queued exactly one,
      // so re-firing would double it.
      if (typeof window.__strxLoadTtq === 'function') window.__strxLoadTtq();
    } catch (_) { /* a TikTok grant fault must never break the page */ }
  };

  const revokeTikTok = () => {
    // A live withdrawal (denied without a reload). Re-arm so a subsequent grant can
    // fire again, and tell TikTok to stop transmitting.
    _tikTokGranted = false;
    if (!window.ttq || typeof window.ttq.revokeConsent !== 'function') return;
    try {
      window.ttq.revokeConsent();
    } catch (_) { /* never break the page */ }
  };

  // The GA4 grant — fetch the deferred gtag.js library on a same-session
  // Accept. Idempotency lives in __strxLoadGtag itself (__strxGtagRequested
  // latch), so no local latch is needed; the returning-consenter path fetched
  // inline in the loader and this call becomes a no-op there. The Consent Mode
  // `update granted` push (from this module) always precedes this in the
  // dataLayer, so the library drains a queue whose consent is already correct
  // and the queued config's automatic page_view transmits exactly once.
  const grantGa4 = () => {
    try {
      if (typeof window.__strxLoadGtag === 'function') window.__strxLoadGtag();
    } catch (_) { /* a GA4 grant fault must never break the page */ }
  };

  document.addEventListener('strx:consent-changed', () => {
    consentState = readConsent();
    // Google's documented opt-out flag also stops automatic SDK events after
    // withdrawal; merely stopping strxTrack would leave enhanced measurement live.
    if (window.__strxGa4Id) window['ga-disable-' + window.__strxGa4Id] = !isGranted();
    try {
      if (typeof window.gtag === 'function') {
        const value = isGranted() ? 'granted' : 'denied';
        window.gtag('consent', 'update', {
          analytics_storage: value, ad_storage: adsGranted() ? 'granted' : 'denied',
          ad_user_data: adsGranted() ? 'granted' : 'denied', ad_personalization: adsGranted() ? 'granted' : 'denied',
        });
      }
    } catch (_) { /* consent changes must still reach the other vendors */ }
    if (isGranted()) grantGa4();
    if (adsGranted()) { grantMeta(); grantTikTok(); }
    else { revokeMeta(); revokeTikTok(); }
    if (isGranted() || adsGranted()) fireEventIslands();
  });

  // A cached page or another tab can outlive the consent cookie. Reconcile
  // when returning to the page, before allowing further interaction events.
  const syncConsentCookie = () => {
    if (window.strxPrivacy) { window.strxPrivacy.read(); return; }
    if (typeof window.__strxConsent !== 'string') return; // tracking excluded
    let value = '';
    try {
      const cookie = document.cookie.split(';').map((part) => part.trim())
        .find((part) => part.startsWith('strx_consent='));
      value = cookie ? decodeURIComponent(cookie.slice('strx_consent='.length)) : '';
    } catch (_) { /* malformed cookie means no consent */ }
    const match = /^\s*1\s*\|\s*(granted|denied)\s*$/i.exec(value);
    const next = match ? match[1].toLowerCase() : 'none';
    if (next === consentState) return;
    window.__strxConsent = next;
    document.dispatchEvent(new CustomEvent('strx:consent-changed', { detail: { granted: next === 'granted' } }));
  };
  window.addEventListener('pageshow', (event) => { if (event.persisted) syncConsentCookie(); });
  window.addEventListener('focus', syncConsentCookie);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') syncConsentCookie();
  });

  // -------------------------------------------------------------
  // 2) The page-load event-island reader.
  //
  // The server emits zero or more INERT islands per page:
  //
  //   <script type="application/json" class="strx-ga4-event"
  //           data-strx-event="view_item_list">{ …GA4 payload… }</script>
  //
  // On DOMContentLoaded we query EVERY `.strx-ga4-event`, parse each one's JSON as
  // the payload, read `data-strx-event` as the event name, and call
  // strxTrack(name, payload) ONCE per island. This is the mechanism for page-load
  // events (Step 3: view_item_list / view_item / search; Steps 4/5 reuse it for
  // begin_checkout + purchase). INTERACTION events — add_to_cart on click — do NOT
  // use an island; they fire from their Alpine handler (Step 4).
  //
  // Resilience contract:
  //   • Multiple islands per page are supported (e.g. the PDP fires view_item AND
  //     a related view_item_list; the home page fires several shelf impressions).
  //   • A malformed / empty island is caught + skipped (logged in Development
  //     only), never throws, never blocks the page or the OTHER islands.
  //   • Each island fires EXACTLY ONCE — we tag a processed island with a data
  //     flag so a re-run (defensive) can never double-fire it.
  //
  // The islands are <script type="application/json">, so the browser never
  // executes them; we read their textContent and JSON.parse it. The server already
  // HTML-safe-encoded the payload (Ga4Serializer), so this is pure data in.
  // -------------------------------------------------------------
  const EVENT_ISLAND_SELECTOR = 'script.strx-ga4-event';
  const ISLAND_DONE_FLAG = 'strxFired';   // dataset key marking an already-fired island

  // Development is signalled by the dev HUD root being present (it renders only in
  // Development on a trackable route). Used purely to gate console.warn noise — it
  // never changes behaviour.
  const isDevelopment = () => document.getElementById('strx-track-hud') !== null;

  const fireEventIslands = () => {
    // Keep the current page's islands pending until consent. This gives all
    // vendors the same page context on Accept without retaining past actions.
    if (!isGranted() && !adsGranted()) return;
    let islands;
    try {
      islands = document.querySelectorAll(EVENT_ISLAND_SELECTOR);
    } catch (_) {
      return;                                // querySelectorAll should never throw — guard anyway
    }

    islands.forEach((island) => {
      // Exactly once: skip an island we've already processed.
      if (!island || island.dataset[ISLAND_DONE_FLAG] === 'true') return;
      island.dataset[ISLAND_DONE_FLAG] = 'true';

      const name = island.getAttribute('data-strx-event');
      if (typeof name !== 'string' || name.length === 0) return;

      // textContent (never innerHTML) — the island is inert JSON data.
      const raw = island.textContent;
      if (typeof raw !== 'string' || raw.trim().length === 0) return;

      let payload;
      try {
        payload = JSON.parse(raw);
      } catch (err) {
        // One malformed island must never break the page or the other islands.
        if (isDevelopment() && window.console && console.warn) {
          console.warn('[strx] skipped a malformed GA4 event island:', name, err);
        }
        return;
      }

      // Forward through the single seam — strxTrack is itself defensive (no-op when
      // gtag is absent), so this is safe even if GA4 hasn't finished loading.
      window.strxTrack(name, payload);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fireEventIslands, { once: true });
  } else {
    fireEventIslands();
  }

  // -------------------------------------------------------------
  // 3) The Development-only diagnostics HUD.
  //
  // Mounts onto the server-rendered #strx-track-hud root (present ONLY in
  // Development on a trackable route). All visible copy comes from data-*
  // attributes on that root, so this file carries no localized strings.
  //
  // The HUD is a small, collapsible dock. It shows:
  //   • the live consent state (window.__strxConsent, updated on the
  //     'strx:consent-changed' event the banner dispatches), and whether
  //     gtag.js has actually loaded;
  //   • a rolling, newest-first list of strxTrack events with their params.
  //
  // It is intentionally lightweight: no framework, transform/opacity-only
  // motion, and it parks itself out of the way. A "fire test event" button
  // lets a developer exercise the seam end-to-end without leaving the page.
  // -------------------------------------------------------------
  const mountHud = () => {
    const root = document.getElementById('strx-track-hud');
    if (!root) return;                       // not Development / not rendered → dormant
    if (root.dataset.mounted === 'true') return;
    root.dataset.mounted = 'true';

    // --- Labels (localized server-side, read from data-*). ----------------
    const L = {
      title:       root.getAttribute('data-label-title')   || 'Tracking',
      consent:     root.getAttribute('data-label-consent') || 'Consent',
      ga4:         root.getAttribute('data-label-ga4')     || 'GA4',
      events:      root.getAttribute('data-label-events')  || 'Events',
      empty:       root.getAttribute('data-label-empty')   || 'No events yet',
      test:        root.getAttribute('data-label-test')    || 'Fire test event',
      collapse:    root.getAttribute('data-label-collapse')|| 'Collapse',
      expand:      root.getAttribute('data-label-expand')  || 'Expand',
      loaded:      root.getAttribute('data-label-loaded')  || 'loaded',
      pending:     root.getAttribute('data-label-pending') || 'pending',
    };

    // --- Element refs (the markup is server-rendered; we only wire it). ----
    const panel    = root.querySelector('[data-hud-panel]');
    const consentEl = root.querySelector('[data-hud-consent]');
    const ga4El    = root.querySelector('[data-hud-ga4]');
    const listEl   = root.querySelector('[data-hud-list]');
    const emptyEl  = root.querySelector('[data-hud-empty]');
    const countEl  = root.querySelector('[data-hud-count]');
    const toggleBtn = root.querySelector('[data-hud-toggle]');
    const testBtn  = root.querySelector('[data-hud-test]');

    let fired = 0;

    // --- Consent / GA4 status reflectors. ---------------------------------
    const STATE_TO_CLASS = { granted: 'is-granted', denied: 'is-denied', none: 'is-none' };

    const reflectConsent = () => {
      if (!consentEl) return;
      // Reuse the module's canonical live consent flag (responsibility #0) —
      // the same truth the Meta fan-out gates on — rather than re-reading the
      // raw window flag here.
      const state = consentState;
      consentEl.textContent = state;
      consentEl.classList.remove('is-granted', 'is-denied', 'is-none');
      consentEl.classList.add(STATE_TO_CLASS[state]);
      consentEl.setAttribute('data-state', state);
    };

    const reflectGa4 = () => {
      if (!ga4El) return;
      // window.gtag always exists (it's the dataLayer-push shim _TrackingHead
      // installs before this file runs), so it is NOT a readiness signal. The
      // honest "gtag.js has actually loaded" signal is that the library has
      // populated its container registry (window.google_tag_manager) — gtag.js
      // sets it once the async <script> from _TrackingGa4 finishes executing.
      // Until then we're still on the shim → "pending".
      const loaded = window.google_tag_manager !== undefined;
      ga4El.textContent = loaded ? L.loaded : L.pending;
      ga4El.classList.toggle('is-granted', loaded);
      ga4El.classList.toggle('is-none', !loaded);
    };

    // --- Event log rendering. ---------------------------------------------
    // Each row is built with textContent ONLY (never innerHTML) so an event
    // name/param can never inject markup into the dev HUD.
    const fmtTime = (ms) => {
      const d = new Date(ms);
      const p = (n) => String(n).padStart(2, '0');
      return p(d.getHours()) + ':' + p(d.getMinutes()) + ':' + p(d.getSeconds());
    };

    const fmtPayload = (payload) => {
      try {
        const json = JSON.stringify(payload);
        if (!json || json === '{}') return '';
        return json.length > 160 ? json.slice(0, 159) + '…' : json;
      } catch (_) { return ''; }
    };

    const addRow = (name, payload, at) => {
      if (!listEl) return;
      if (emptyEl) emptyEl.hidden = true;

      const row = document.createElement('li');
      row.className = 'strx-track-hud__event';

      const head = document.createElement('div');
      head.className = 'strx-track-hud__event-head';

      const nameEl = document.createElement('span');
      nameEl.className = 'strx-track-hud__event-name';
      nameEl.textContent = name;

      const timeEl = document.createElement('time');
      timeEl.className = 'strx-track-hud__event-time';
      timeEl.textContent = fmtTime(at);

      head.appendChild(nameEl);
      head.appendChild(timeEl);
      row.appendChild(head);

      const detail = fmtPayload(payload);
      if (detail) {
        const detailEl = document.createElement('code');
        detailEl.className = 'strx-track-hud__event-detail';
        detailEl.textContent = detail;
        row.appendChild(detailEl);
      }

      // Newest first.
      listEl.insertBefore(row, listEl.firstChild);

      // Trim the rendered list to the same cap as the ring.
      while (listEl.children.length > RING_MAX) {
        listEl.removeChild(listEl.lastChild);
      }

      fired += 1;
      if (countEl) countEl.textContent = String(fired);

      // Brief highlight pulse on the freshest row (CSS-driven).
      row.classList.add('is-fresh');
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => row.classList.remove('is-fresh'));
      });
    };

    // --- Wire events. ------------------------------------------------------
    // Replay any events that fired BEFORE the HUD mounted (e.g. a strxTrack call
    // earlier in page load) FIRST, from a snapshot of the ring, THEN attach the
    // live listener. Ordering it this way means a row can never be rendered
    // twice — even if mount were ever made async — because the replay set and
    // the live stream are strictly disjoint (the listener only sees events
    // dispatched after this point). Snapshot now so a concurrent push can't be
    // both replayed and delivered live.
    const backlog = recent.slice();

    document.addEventListener(HUD_EVENT, (event) => {
      const d = event.detail || {};
      addRow(d.name || '?', d.payload || {}, Date.now());
    });

    // Replayed oldest→newest so the log reads chronologically (addRow prepends).
    backlog.forEach((e) => addRow(e.name, e.payload, e.at));

    document.addEventListener('strx:consent-changed', () => {
      reflectConsent();
      reflectGa4();
    });

    // Collapse / expand the panel (state persisted in sessionStorage so it
    // survives the full-page navigations this server-rendered app does).
    const COLLAPSE_KEY = 'strx-track-hud-collapsed';
    const setCollapsed = (collapsed) => {
      root.classList.toggle('is-collapsed', collapsed);
      if (toggleBtn) {
        toggleBtn.setAttribute('aria-expanded', String(!collapsed));
        toggleBtn.setAttribute('aria-label', collapsed ? L.expand : L.collapse);
      }
      if (panel) panel.hidden = collapsed;
      try { window.sessionStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch (_) { /* ignore */ }
    };
    if (toggleBtn) {
      toggleBtn.addEventListener('click', () => {
        setCollapsed(!root.classList.contains('is-collapsed'));
      });
    }

    if (testBtn) {
      testBtn.addEventListener('click', () => {
        // A harmless, clearly-marked diagnostic event. GA4 will receive it in
        // DebugView (debug_mode is on in Development), proving the whole seam.
        window.strxTrack('strx_debug_ping', {
          source: 'dev_hud',
          ts: Date.now(),
        });
      });
    }

    // --- Initial paint. ----------------------------------------------------
    reflectConsent();
    reflectGa4();
    // gtag.js loads async — poll a couple of times so the GA4 "loaded" pill
    // flips without needing an event. Cheap + bounded (stops once loaded or
    // after the window elapses).
    let pollsLeft = 12;                 // ~12 × 500ms = 6s, plenty for gtag.js
    const poll = window.setInterval(() => {
      reflectGa4();
      pollsLeft -= 1;
      const isLoaded = ga4El && ga4El.classList.contains('is-granted');
      if (isLoaded || pollsLeft <= 0) window.clearInterval(poll);
    }, 500);

    // Restore the collapsed preference.
    let startCollapsed = false;
    try { startCollapsed = window.sessionStorage.getItem(COLLAPSE_KEY) === '1'; } catch (_) { /* ignore */ }
    setCollapsed(startCollapsed);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountHud, { once: true });
  } else {
    mountHud();
  }
})();
